import { useMemo, useEffect, useState } from 'react'
import { TrendingUp, Calendar, BarChart3, Users } from 'lucide-react'
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  LabelList,
  ReferenceLine,
  Cell,
} from 'recharts'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from '@/shared/components/ui/chart'
import type { DashboardGraficos } from '../types'

const TEAL_PALETTE_LIGHT = [
  '#115e59',
  '#0f766e',
  '#0d9488',
  '#14b8a6',
  '#2dd4bf',
  '#5eead4',
  '#99f6e4',
  '#ccfbf1',
]

const TEAL_PALETTE_DARK = [
  '#ccfbf1',
  '#99f6e4',
  '#5eead4',
  '#2dd4bf',
  '#14b8a6',
  '#0d9488',
  '#0f766e',
  '#115e59',
]

const parseDateSafe = (dateString: string): Date => {
  if (dateString.includes('T')) return new Date(dateString)
  return new Date(dateString + 'T12:00:00')
}

interface DashboardChartsProps {
  data: DashboardGraficos | undefined
  isLoading: boolean
  activeConfigId?: string
  tooltipLabel?: string
  isCurrency?: boolean
}

function AreaChartCard({ data, isLoading, tooltipLabel = 'registros', isCurrency = false }: { data: { fecha: string; total: number }[]; isLoading: boolean; tooltipLabel?: string; isCurrency?: boolean }) {
  const formattedData = useMemo(() => {
    return (data || []).map((item) => ({
      ...item,
      fechaDisplay: parseDateSafe(item.fecha).toLocaleDateString('es-EC', { day: '2-digit', month: 'short' }),
    }))
  }, [data])

  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
        <CardContent><div className="h-[280px] flex items-center justify-center"><div className="animate-pulse text-muted-foreground text-xs">Cargando...</div></div></CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><TrendingUp className="h-4 w-4 text-muted-foreground" />Fechas</CardTitle></CardHeader>
        <CardContent><div className="h-[280px] flex flex-col items-center justify-center text-muted-foreground"><Calendar className="h-6 w-6 mb-2 text-muted-foreground/50" /><p className="text-xs font-medium">No hay datos de fechas</p></div></CardContent>
      </Card>
    )
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden w-full h-full flex flex-col">
      <CardHeader className="pb-0 pt-3 shrink-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="h-4 w-4 text-muted-foreground" />
          Fechas
        </CardTitle>
      </CardHeader>
      <CardContent className="p-0 px-1 overflow-hidden flex-1 min-h-0">
        <ChartContainer config={{ total: { label: tooltipLabel.charAt(0).toUpperCase() + tooltipLabel.slice(1), color: '#0d9488' } } satisfies ChartConfig} className="!aspect-auto h-full w-full max-w-full">
          <AreaChart data={formattedData} margin={{ top: 30, right: isCurrency ? 30 : 15, left: 5, bottom: 15 }}>
            <defs>
              <linearGradient id="colorTotalDash" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#0d9488" stopOpacity={0.2} />
                <stop offset="95%" stopColor="#0d9488" stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-muted" />
            <XAxis dataKey="fechaDisplay" tickLine={false} axisLine={false} tickMargin={8} interval="preserveStartEnd" className="text-xs" />
            <YAxis tickLine={false} axisLine={false} tick={false} width={10} />
            <ChartTooltip
              cursor={{ stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1, strokeDasharray: '4 4' }}
              content={
                <ChartTooltipContent
                  labelFormatter={(_, payload) => {
                    if (payload?.[0]?.payload?.fecha) {
                      return parseDateSafe(payload[0].payload.fecha).toLocaleDateString('es-EC', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
                    }
                    return ''
                  }}
                  formatter={isCurrency ? (value) => {
                    const num = Number(value)
                    const formatted = '$ ' + num.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    return formatted
                  } : undefined}
                />
              }
            />
            <Area
              type="monotone"
              dataKey="total"
              stroke="#0d9488"
              strokeWidth={2}
              fill="url(#colorTotalDash)"
              dot={(props) => {
                const { cx, cy, payload } = props
                if (payload.total > 0) return <circle cx={cx} cy={cy} r={3} fill="#0d9488" stroke="white" strokeWidth={1.5} />
                return <g />
              }}
              activeDot={{ r: 5, fill: '#0d9488', stroke: 'white', strokeWidth: 2 }}
            >
              <LabelList
                dataKey="total"
                position="top"
                offset={8}
                content={(props) => {
                  const { x, y, value } = props as { x: number; y: number; value: number }
                  const num = Number(value)
                  if (num <= 0) return <g />
                  let label: string
                  if (isCurrency) {
                    const formatted = Number.isInteger(num) ? num.toLocaleString('es-EC') : num.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    label = `$ ${formatted}`
                  } else {
                    label = String(num)
                  }
                  return (
                    <text x={x} y={y - 8} textAnchor="middle" className="fill-gray-700 dark:fill-white" fontSize={11} fontWeight={600}>
                      {label}
                    </text>
                  )
                }}
              />
            </Area>
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}

function BarChartCard({
  title,
  data,
  isLoading,
  tooltipLabel = 'registros',
  fillHeight,
  maxHeight,
  isCurrency = false,
}: {
  title: string
  data: { nombre: string; total: number }[]
  isLoading: boolean
  tooltipLabel?: string
  fillHeight?: boolean
  maxHeight?: number
  isCurrency?: boolean
}) {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'))

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'))
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const { chartConfig, chartData } = useMemo(() => {
    const palette = isDark ? TEAL_PALETTE_DARK : TEAL_PALETTE_LIGHT
    const config: ChartConfig = { total: { label: tooltipLabel.charAt(0).toUpperCase() + tooltipLabel.slice(1) } }
    const formatted = (data || []).map((item, index) => {
      const key = `cat-${index}`
      const maxIndex = Math.min(palette.length - 1, data.length + 1)
      const colorIndex = data.length <= 1 ? 0 : Math.round((index / (data.length - 1)) * maxIndex)
      config[key] = { label: item.nombre, color: palette[colorIndex] }
      return {
        ...item,
        displayName: item.nombre.length > 25 ? item.nombre.substring(0, 25) + '...' : item.nombre,
        fill: `var(--color-${key})`,
      }
    })
    return { chartConfig: config, chartData: formatted }
  }, [data, isDark])

  const totalSum = useMemo(() => (data || []).reduce((sum, item) => sum + item.total, 0), [data])

  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
        <CardContent><div className="h-[150px] flex items-center justify-center"><div className="animate-pulse text-muted-foreground text-xs">Cargando...</div></div></CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><BarChart3 className="h-4 w-4 text-muted-foreground" />{title}</CardTitle></CardHeader>
        <CardContent><div className="h-[150px] flex flex-col items-center justify-center text-muted-foreground"><BarChart3 className="h-6 w-6 mb-2 text-muted-foreground/50" /><p className="text-xs font-medium">No hay datos</p></div></CardContent>
      </Card>
    )
  }

  const MAX_VISIBLE = 8
  const BAR_HEIGHT = 40
  const defaultContainerHeight = MAX_VISIBLE * BAR_HEIGHT + 20
  const chartHeight = data.length * BAR_HEIGHT + 20

  return (
    <Card className={`border border-gray-300 dark:border-gray-700 overflow-hidden w-full ${fillHeight ? 'flex flex-col h-full' : ''}`} style={maxHeight ? { maxHeight: `${maxHeight}px` } : undefined}>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          {title}
          <Badge variant="outline" className="text-xs font-semibold tabular-nums border-[0.5px] border-teal-500 rounded-full">
            {data.length}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className={fillHeight ? 'flex-1 overflow-hidden' : ''}>
        <div className="overflow-y-auto" style={{ maxHeight: `${defaultContainerHeight}px` }}>
          <ChartContainer config={chartConfig} style={{ height: `${chartHeight}px` }} className="w-full">
            <BarChart accessibilityLayer data={chartData} layout="vertical" margin={{ left: 0, right: isCurrency ? 70 : 40 }}>
              <YAxis dataKey="displayName" type="category" tickLine={false} tickMargin={6} axisLine={false} width={140} tick={{ fontSize: 11 }} interval={0} />
              <XAxis dataKey="total" type="number" hide />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    formatter={(value, _name, item) => {
                      const percentage = ((Number(value) / totalSum) * 100).toFixed(1)
                      const num = Number(value)
                      const formatted = isCurrency
                        ? '$ ' + num.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                        : num.toLocaleString('es-EC')
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-foreground">{item.payload.nombre}</span>
                          <span className="text-muted-foreground">{formatted} {tooltipLabel} ({percentage}%)</span>
                        </div>
                      )
                    }}
                    hideLabel
                  />
                }
              />
              <Bar dataKey="total" layout="vertical" radius={5}>
                <LabelList
                  dataKey="total"
                  position="right"
                  offset={6}
                  content={(props) => {
                    const { x, y, width, height, value } = props as { x: number; y: number; width: number; height: number; value: number }
                    const num = Number(value)
                    const formatted = Number.isInteger(num) ? num.toLocaleString('es-EC') : num.toLocaleString('es-EC', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
                    const label = isCurrency ? `$ ${formatted}` : formatted
                    return (
                      <text
                        x={x + width + 6}
                        y={y + height / 2}
                        dominantBaseline="central"
                        className="fill-gray-700 dark:fill-white"
                        fontSize={11}
                        fontWeight={600}
                      >
                        {label}
                      </text>
                    )
                  }}
                />
              </Bar>
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  )
}

const META_PUNTOS = 300
const COLOR_META = '#0d9488'    // teal
const COLOR_EN_PROCESO = '#b45309' // amber-700

function ParticipantesPuntosChart({ data, isLoading }: { data: { nombre: string; total: number }[]; isLoading: boolean }) {
  if (isLoading) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader><Skeleton className="h-5 w-32" /></CardHeader>
        <CardContent><div className="h-[280px] flex items-center justify-center"><div className="animate-pulse text-muted-foreground text-xs">Cargando...</div></div></CardContent>
      </Card>
    )
  }

  if (!data || data.length === 0) {
    return (
      <Card className="border border-gray-300 dark:border-gray-700">
        <CardHeader><CardTitle className="flex items-center gap-2 text-base"><Users className="h-4 w-4 text-muted-foreground" />Puntos por Participante</CardTitle></CardHeader>
        <CardContent><div className="h-[280px] flex flex-col items-center justify-center text-muted-foreground"><Users className="h-6 w-6 mb-2 text-muted-foreground/50" /><p className="text-xs font-medium">No hay datos</p></div></CardContent>
      </Card>
    )
  }

  const BAR_HEIGHT = 40
  const chartHeight = data.length * BAR_HEIGHT + 40
  const metaCount = data.filter((d) => d.total >= META_PUNTOS).length

  const chartConfig: ChartConfig = {
    total: { label: 'Puntos' },
  }

  return (
    <Card className="border border-gray-300 dark:border-gray-700 overflow-hidden w-full h-full flex flex-col">
      <CardHeader className="pb-2 shrink-0">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <Users className="h-4 w-4 text-muted-foreground" />
            Puntos por Participante
          </CardTitle>
          <Badge variant="outline" className="text-xs font-semibold tabular-nums border-[0.5px] border-teal-500 rounded-full">
            {metaCount}/{data.length}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="flex-1 overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: '400px' }}>
          <ChartContainer config={chartConfig} style={{ height: `${chartHeight}px` }} className="w-full">
            <BarChart data={data} layout="vertical" margin={{ left: 0, right: 50 }}>
              <YAxis
                dataKey="nombre"
                type="category"
                tickLine={false}
                tickMargin={6}
                axisLine={false}
                width={140}
                tick={{ fontSize: 11 }}
                interval={0}
              />
              <XAxis dataKey="total" type="number" hide />
              <ChartTooltip
                cursor={false}
                content={
                  <ChartTooltipContent
                    formatter={(value, _name, item) => {
                      const num = Number(value)
                      const cumple = num >= META_PUNTOS
                      return (
                        <div className="flex flex-col gap-0.5">
                          <span className="font-medium text-foreground">{item.payload.nombre}</span>
                          <span className="text-muted-foreground">{num.toLocaleString('es-EC')} puntos</span>
                          <span style={{ color: cumple ? COLOR_META : COLOR_EN_PROCESO, fontWeight: 600 }}>
                            {cumple ? 'Meta cumplida' : 'En proceso'}
                          </span>
                        </div>
                      )
                    }}
                    hideLabel
                  />
                }
              />
              <Bar dataKey="total" layout="vertical" radius={5}>
                {data.map((entry, index) => (
                  <Cell key={index} fill={entry.total >= META_PUNTOS ? COLOR_META : COLOR_EN_PROCESO} />
                ))}
                <LabelList
                  dataKey="total"
                  position="right"
                  offset={6}
                  content={(props) => {
                    const { x, y, width, height, value } = props as { x: number; y: number; width: number; height: number; value: number }
                    const num = Number(value)
                    return (
                      <text
                        x={x + width + 6}
                        y={y + height / 2}
                        dominantBaseline="central"
                        className="fill-gray-700 dark:fill-white"
                        fontSize={11}
                        fontWeight={600}
                      >
                        {num.toLocaleString('es-EC')}
                      </text>
                    )
                  }}
                />
              </Bar>
              <ReferenceLine
                x={META_PUNTOS}
                stroke="#e11d48"
                strokeWidth={2}
                strokeDasharray="6 4"
                isFront
                label={{ value: `Meta ${META_PUNTOS}`, position: 'top', fontSize: 11, fill: '#e11d48', fontWeight: 600 }}
              />
            </BarChart>
          </ChartContainer>
        </div>
      </CardContent>
    </Card>
  )
}

export function DashboardCharts({ data, isLoading, tooltipLabel = 'registros', isCurrency = false }: DashboardChartsProps) {
  const graficas = data?.graficas || []
  const hasParticipantesPuntos = !!data?.participantes_puntos && data.participantes_puntos.length > 0

  // Cuando es Participantes: gráfica de puntos ocupa el espacio de la línea
  if (hasParticipantesPuntos) {
    const firstBar = graficas[0]
    const restBars = graficas.slice(1)
    return (
      <div className="space-y-3 mt-3">
        <div className="grid grid-cols-10 gap-3 items-stretch">
          <div className="col-span-7">
            <ParticipantesPuntosChart data={data!.participantes_puntos!} isLoading={isLoading} />
          </div>
          <div className="col-span-3 flex">
            {firstBar ? (
              <BarChartCard title={firstBar.titulo} data={firstBar.datos} isLoading={isLoading} tooltipLabel={tooltipLabel} fillHeight isCurrency={isCurrency} />
            ) : (
              <BarChartCard title="" data={[]} isLoading={isLoading} tooltipLabel={tooltipLabel} fillHeight />
            )}
          </div>
        </div>
        {restBars.length > 0 && (
          <div className={`grid gap-3 ${restBars.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
            {restBars.map((g, i) => (
              <BarChartCard key={i} title={g.titulo} data={g.datos} isLoading={isLoading} tooltipLabel={tooltipLabel} maxHeight={400} isCurrency={isCurrency} />
            ))}
          </div>
        )}
      </div>
    )
  }

  const firstBar = graficas[0]
  const restBars = graficas.slice(1)

  return (
    <div className="space-y-3 mt-3">
      <div className="grid grid-cols-10 gap-3 items-stretch">
        <div className="col-span-7">
          <AreaChartCard data={data?.por_fecha || []} isLoading={isLoading} tooltipLabel={tooltipLabel} isCurrency={isCurrency} />
        </div>
        <div className="col-span-3 flex">
          {firstBar ? (
            <BarChartCard title={firstBar.titulo} data={firstBar.datos} isLoading={isLoading} tooltipLabel={tooltipLabel} fillHeight isCurrency={isCurrency} />
          ) : (
            <BarChartCard title="" data={[]} isLoading={isLoading} tooltipLabel={tooltipLabel} fillHeight />
          )}
        </div>
      </div>
      {restBars.length > 0 && (
        <div className={`grid gap-3 ${restBars.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`}>
          {restBars.map((g, i) => (
            <BarChartCard key={i} title={g.titulo} data={g.datos} isLoading={isLoading} tooltipLabel={tooltipLabel} maxHeight={400} isCurrency={isCurrency} />
          ))}
        </div>
      )}
    </div>
  )
}
